import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePrompt } from "../ConfirmDialog";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
};

/**
 * Lightweight rich-text editor on TipTap. Stores content as HTML.
 * Toolbar: bold, italic, underline (via strike), bullet/ordered list, link, clear.
 * Sanitization is unnecessary for Bankom-internal authors writing for Bankom-internal
 * readers; still, the StarterKit excludes raw HTML by default.
 */
export function RichTextEditor({ value, onChange, placeholder, minHeight = 160 }: Props) {
  const { t } = useTranslation();
  const showPrompt = usePrompt();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "tm-rte-content",
        style: `min-height: ${minHeight}px; padding: 10px 12px; outline: none;`,
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Setting the content from the outside is not the author typing, so it raises no update
      // and does not push the previous text onto the undo stack.
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  function btn(label: string, active: boolean, onClick: () => void) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        style={{
          padding: "4px 8px",
          border: "1px solid var(--border)",
          borderRadius: 4,
          background: active ? "var(--brand-primary)" : "var(--surface)",
          color: active ? "var(--on-primary)" : "var(--ink-2)",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  }

  async function addLink() {
    const prev = editor!.getAttributes("link").href as string | undefined;
    const url = await showPrompt({ message: t("tm.rte.linkPrompt", "URL"), defaultValue: prev ?? "https://" });
    if (url === null) return;
    if (url === "") {
      editor!.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor!.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface)" }}>
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 4,
        padding: "6px 8px",
        borderBottom: "1px solid var(--border)",
        background: "var(--surface-2)",
        borderRadius: "8px 8px 0 0",
      }}>
        {btn("B", editor.isActive("bold"), () => editor.chain().focus().toggleBold().run())}
        {btn("I", editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run())}
        {btn("S", editor.isActive("strike"), () => editor.chain().focus().toggleStrike().run())}
        {btn("H2", editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run())}
        {btn("H3", editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run())}
        {btn("• List", editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run())}
        {btn("1. List", editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run())}
        {btn(">", editor.isActive("blockquote"), () => editor.chain().focus().toggleBlockquote().run())}
        {btn("</>", editor.isActive("codeBlock"), () => editor.chain().focus().toggleCodeBlock().run())}
        {btn(t("tm.rte.link", "Link"), editor.isActive("link"), addLink)}
        {btn(t("tm.rte.clear", "Clear"), false, () => editor.chain().focus().clearNodes().unsetAllMarks().run())}
      </div>
      <div style={{ position: "relative" }}>
        {editor.isEmpty && placeholder && (
          <div style={{
            position: "absolute", top: 10, left: 12,
            color: "var(--ink-4)", fontSize: 14,
            pointerEvents: "none",
          }}>
            {placeholder}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
